import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Loading from '@/components/common/Loading'

export default function CalendarEventSelector({ team, onEventSelected, onSkip, onCancel }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [usedEventIds, setUsedEventIds] = useState(new Set())

  useEffect(() => {
    fetchEvents()
  }, [team?.id])

  const fetchEvents = async () => {
    if (!team?.id) return

    setLoading(true)
    setError(null)

    try {
      // Calculate date range (next 60 days)
      const now = new Date()
      const sixtyDaysFromNow = new Date()
      sixtyDaysFromNow.setDate(now.getDate() + 60)

      // Fetch calendar events
      const { data: eventsData, error: eventsError } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('team_id', team.id)
        .in('event_type', ['practice', 'other'])
        .gte('start_time', now.toISOString())
        .lte('start_time', sixtyDaysFromNow.toISOString())
        .order('start_time', { ascending: true })

      if (eventsError) throw eventsError

      // Fetch existing postings to check which events are already used
      const { data: postingsData, error: postingsError } = await supabase
        .from('scrimmage_postings')
        .select('calendar_event_id')
        .eq('team_id', team.id)
        .not('calendar_event_id', 'is', 'null')

      if (postingsError) throw postingsError

      const usedIds = new Set(postingsData.map(p => p.calendar_event_id))
      setUsedEventIds(usedIds)
      setEvents(eventsData || [])
    } catch (err) {
      console.error('Error fetching calendar events:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <Loading text="Loading calendar events..." />
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <h2 className="font-heading text-xl font-bold mb-4">
          Import from Calendar
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Failed to load calendar events</p>
          <p className="text-sm text-brand-slate mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="secondary" onClick={onCancel}>
              Go Back
            </Button>
            <Button variant="primary" onClick={onSkip}>
              Create Blank Posting
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  if (!team?.calendar_feed_url) {
    return (
      <Card>
        <h2 className="font-heading text-xl font-bold mb-4">
          Import from Calendar
        </h2>
        <div className="text-center py-8">
          <p className="text-brand-slate mb-4">
            No calendar synced yet. Set up calendar sync in Settings to import events.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="secondary" onClick={onCancel}>
              Go Back
            </Button>
            <Button variant="primary" onClick={onSkip}>
              Create Blank Posting
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  if (events.length === 0) {
    return (
      <Card>
        <h2 className="font-heading text-xl font-bold mb-4">
          Import from Calendar
        </h2>
        <div className="text-center py-8">
          <p className="text-brand-slate mb-4">
            No practice or ice time events found in the next 60 days.
          </p>
          <p className="text-sm text-brand-slate mb-6">
            Make sure your calendar is synced and contains upcoming practice events.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="secondary" onClick={onCancel}>
              Go Back
            </Button>
            <Button variant="primary" onClick={onSkip}>
              Create Blank Posting
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-2">
          Import from Calendar
        </h2>
        <p className="text-brand-slate text-sm">
          Select a practice or ice time to convert into a scrimmage posting. You can edit all details before posting.
        </p>
      </div>

      <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
        {events.map(event => {
          const isUsed = usedEventIds.has(event.id)
          
          return (
            <button
              key={event.id}
              onClick={() => onEventSelected(event)}
              className="w-full text-left p-4 border border-border rounded-lg hover:bg-brand-cream/40 transition focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-brand-charcoal">
                      {event.title}
                    </h3>
                    {isUsed && (
                      <Badge variant="default">
                        Already Posted
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-brand-slate mb-1">
                    {formatDate(event.start_time)} • {formatTime(event.start_time)}
                    {event.end_time && ` - ${formatTime(event.end_time)}`}
                  </p>
                  
                  {event.location && (
                    <p className="text-sm text-brand-slate mb-1">
                      📍 {event.location}
                    </p>
                  )}
                  
                  {event.description && (
                    <p className="text-sm text-brand-slate italic mt-2">
                      {event.description}
                    </p>
                  )}
                </div>
                
                <div className="text-brand-green text-sm font-medium whitespace-nowrap">
                  Select →
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-border">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={onSkip}>
          Skip - Create Blank Posting
        </Button>
      </div>
    </Card>
  )
}

